import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus } from "@/hooks/useAuth";
import { NoindexSeo } from "@/components/Seo";

/**
 * Garde de route protégée : redirige vers /login si non authentifié.
 * Si l'utilisateur est authentifié mais n'a pas vérifié son email,
 * il est redirigé vers /verify-email (RG-AUTH-02).
 *
 * noindex : toutes les pages derrière cette garde sont privées (logements,
 * DPE, résultats, compte) — on interdit leur indexation par les moteurs.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user, needsEmailVerification } = useAuthStatus();
  const location = useLocation();

  if (isLoading) {
    return (
      <>
        <NoindexSeo />
        <FullPageLoader />
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user && user.email_verified_at === null) {
    return <Navigate to="/verify-email" replace />;
  }

  if (needsEmailVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  return (
    <>
      <NoindexSeo />
      {children}
    </>
  );
}

function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center text-gray-500">
      Chargement…
    </div>
  );
}
